<template>
  <div>
    <div ref="turnstileContainer" />
    <validation-errors v-if="showValidationError" :errors="[validationErrorMessage]" class="mb-1" />
    <submit-button :form-valid="formValid && challengePassed"
                   :label="label" :submitting-label="submittingLabel"
                   :submit-in-progress="submitInProgress" :class="buttonClass"
                   @click="submit" />
  </div>
</template>

<script>
import { nextTick } from 'vue'
import SubmitButton from './submit-button'
import ValidationErrors from '@/components/inputs/validation-errors'

const FLEXIBLE_WIDGET_MIN_WIDTH = 300
const MOBILE_WINDOW_BUTTON_WIDTH_DIFFERENCE = 56

export default {
  components: { SubmitButton, ValidationErrors },

  props: {
    // This prop needs to be named `buttonClass` rather than `class` because ESLint will
    // throw a vue/no-parsing-error error if a prop is named `class`.
    buttonClass: {
      type: String,
      required: false
    },
    formValid: {
      type: Boolean,
      required: false,
      default: true
    },
    label: {
      type: String,
      required: true
    },
    submitInProgress: {
      type: Boolean,
      required: false,
      default: false
    },
    submittingLabel: {
      type: String,
      required: false
    },
    turnstileAction: {
      type: String,
      required: false
    },
    validationErrors: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },

  emits: ['submit-with-token'],

  data() {
    return {
      challengeToken: null,
      elementWidth: null,
      widgetId: null
    }
  },

  computed: {
    challengePassed() {
      return !!this.challengeToken
    },

    showValidationError() {
      return !!this.validationErrors.turnstileVerifyResult
    },

    validationErrorMessage() {
      return this.i18n.t('public.bot_protection.rejected', {org_name: this.agraConfiguration.organisationName})
    },

    widgetSize() {
      // What size of Turnstile widget should we use?
      // See https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/#widget-size
      if (this.elementWidth >= FLEXIBLE_WIDGET_MIN_WIDTH) {
        // The "flexible" size has a minimum width of 300px and stretches to 100% of the containing element
        return 'flexible'
      } else {
        // The "compact" size is 150px wide
        return 'compact'
      }
    }
  },

  watch: {
    submitInProgress() {
      if (!this.submitInProgress) {
        // The submit has finished, and we're still here.
        // This presumably means that the submit _failed_, and we may need to re-submit.
        // Reset the challenge so we're ready.
        this.challengeToken = null
        /* eslint-disable-next-line no-undef  */
        turnstile.reset(this.widgetId)
      }
    }
  },

  created() {
    // Lazy-load the turnstile initialization script.
    const { waitForTurnstileLoaded } = require('@/turnstile')
    this.waitForTurnstileLoaded = waitForTurnstileLoaded
  },

  mounted() {
    nextTick(() => {
      this.elementWidth = this.$el.offsetWidth
      if (this.elementWidth === 0) {
        // On mobile, the form is hidden at first, which can cause offsetWidth to return 0.
        // In that case, infer the form width from the window size.
        this.elementWidth = window.innerWidth - MOBILE_WINDOW_BUTTON_WIDTH_DIFFERENCE
      }
    })

    this.waitForTurnstileLoaded(200).then(() => {
      this.renderTurnstile()
    })
  },

  beforeUnmount() {
    /* eslint-disable-next-line no-undef  */
    turnstile.remove(this.widgetId)
  },

  methods: {
    handleChallengeSuccess(token) {
      this.challengeToken = token
    },

    handleExpiredChallenge() {
      // Since the challenge has expired, any token we had is no longer valid.
      this.challengeToken = null

      // Reset the widget so we can solve a new challenge and get a new token.
      /* eslint-disable-next-line no-undef  */
      turnstile.reset(this.widgetId)
    },

    renderTurnstile() {
      /* eslint-disable-next-line no-undef  */
      this.widgetId = turnstile.render(this.$refs.turnstileContainer, {
        sitekey: this.agraConfiguration.botProtection.siteKey,
        action: this.turnstileAction,
        callback: this.handleChallengeSuccess,
        'expired-callback': this.handleExpiredChallenge,
        appearance: 'interaction-only',
        size: this.widgetSize
      })
    },

    submit() {
      this.$emit('submit-with-token', {turnstileToken: this.challengeToken})
    }
  }
}
</script>
